<template>
    <div>
        <v-container grid-list-xl :class="$vuetify.breakpoint.smAndDown ? 'pa-0' : ''">
            <stepper class="elevation-0" @input="refreshMeta" ref="stepper">
                <v-stepper-header class="elevation-0" v-show="!hideHeader && $vuetify.breakpoint.mdAndUp">
                    <v-stepper-step :step="1">{{ 'address' | translation }}</v-stepper-step>

                    <v-divider/>

                    <v-stepper-step :step="2">{{ 'Payment' | translation }}</v-stepper-step>

                    <v-divider/>

                    <v-stepper-step :step="3">{{ 'Finished' | translation }}</v-stepper-step>
                </v-stepper-header>

                <v-card-title v-if="!prerenderMode && $vuetify.breakpoint.smAndDown" class="justify-center checkout-mobile-title">
                    <h3 class="display-1 primary--text">{{ title | translation }}</h3>

                    <router-link to="/basket" class="checkout-close-icon text-decoration-none d-flex fill-height pa-2">
                        <v-icon color="primary">close</v-icon>
                    </router-link>
                </v-card-title>

                <v-stepper-items v-if="!prerenderMode">
                    <address-step :step="1" :title.sync="title"/>
                    <summary-step :step="2" :title.sync="title" @onNextStep="onPaymentSuccess" flat-cards/>
                    <payment-processing-step :step="2.5" :title.sync="title" @onNextStep="onPaymentSuccess"/>
                    <success-step :step="3" :title.sync="title" :order="order" route="success" :paid="orderIsPaid"/>
                </v-stepper-items>
            </stepper>
        </v-container>
    </div>
</template>

<style lang="stylus">
@import '~vuetify/src/stylus/settings/_colors.styl';

.checkout-mobile-title {
    position: relative;
    border-bottom: 1px solid $grey.lighten-3;
}

.checkout-close-icon {
    position: absolute;
    top: 0;
    right: 0;
}
</style>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import AddressStep from './AddressStep/AddressStep';
import PaymentProcessingStep from './PaymentProcessingStep';
import CheckoutRegistrationDefault from './AddressStep/Registration/default/Registration';
import CheckoutRegistrationDe from './AddressStep/Registration/de/Registration';
import CheckoutRegistrationIt from './AddressStep/Registration/it/Registration';
import CheckoutRegistrationBe from './AddressStep/Registration/be/Registration';
import BillingAddressDefault from './AddressStep/BillingAddress';
import ShippingAddressDefault from './AddressStep/ShippingAddress';
import {eventManager} from '@breakmedia/prime-vue-event-manager';

const moduleName = 'Checkout';

const stepMetaMapper = {
    1: {
        title: 'META_CHECKOUT_ADDRESS_STEP_TITLE',
        description: 'META_CHECKOUT_ADDRESS_STEP_DESCRIPTION',
    },
    2: {
        title: 'META_CHECKOUT_SUMMARY_STEP_TITLE',
        description: 'META_CHECKOUT_SUMMARY_STEP_DESCRIPTION',
    },
    2.5: {
        title: 'META_CHECKOUT_PAYMENT_PROCESS_STEP_TITLE',
        description: 'META_CHECKOUT_PAYMENT_PROCESS_STEP_DESCRIPTION',
    },
    3: {
        title: 'META_CHECKOUT_SUCCESS_STEP_TITLE',
        description: 'META_CHECKOUT_SUCCESS_STEP_DESCRIPTION',
    },
};

export default {
    name: moduleName,
    components: {
        AddressStep,
        PaymentProcessingStep,
    },
    data() {
        return {
            currentStepId: null,
            order: {},
            title: null,
            orderIsPaid: null,
        };
    },
    beforeCreate() {
        if (!Vue.options.components['checkout-registration-default']) {
            Vue.component('checkout-registration-default', CheckoutRegistrationDefault);
        }
        if (!Vue.options.components['checkout-registration-de']) {
            Vue.component('checkout-registration-de', CheckoutRegistrationDe);
        }
        if (!Vue.options.components['checkout-registration-it']) {
            Vue.component('checkout-registration-it', CheckoutRegistrationIt);
        }
        if (!Vue.options.components['checkout-registration-be']) {
            Vue.component('checkout-registration-be', CheckoutRegistrationBe);
        }
        if (!Vue.options.components['checkout-billing-address-default']) {
            Vue.component('checkout-billing-address-default', BillingAddressDefault);
        }
        if (!Vue.options.components['checkout-shipping-address-default']) {
            Vue.component('checkout-shipping-address-default', ShippingAddressDefault);
        }
    },
    created() {
        this.$currency.lockChange();

        if (this.$route.query.transaction_id) {
            this.onPaymentSuccess({ transaction_id: this.$route.query.transaction_id }).then(() => {
                this.$router.replace({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        transaction_id: undefined,
                    },
                });
            });
        }
    },
    destroyed() {
        this.$currency.unlockChange();
    },
    props: {
        hideHeader: Boolean,
    },
    computed: {
        ...mapState('PageContent', [
            'prerenderMode'
        ]),
        isBasketEmpty() {
            return this.$basket.isBasketEmpty();
        }
    },
    methods: {
        onPaymentSuccess(orderData) {
            return Vue.$order.getOrderByFilters({
                transactionId: orderData.transaction_id,
                include_paid: true,
            }).then(order => {
                this.order = order;
                this.$basket.deleteBasket();

                if (orderData.paid === false) {
                    this.orderIsPaid = false;
                }

                eventManager.$emit('purchase', {
                    order,
                    eventId: eventManager.getUniqueId('purchase'),
                });
            });
        },
        refreshMeta(stepId) {
            if (typeof stepId === 'number') {
                let triggerRefreshMeta = this.currentStepId !== null;
                this.currentStepId = stepId;
                if (triggerRefreshMeta) {
                    this.$meta().refresh();
                }
            }
        },
    },
    metaInfo() {
        if (stepMetaMapper[this.currentStepId] === undefined) {
            return {};
        }
        return {
            title: this.$language.translate(stepMetaMapper[this.currentStepId].title),
            meta: [
                {
                    vmid: 'description',
                    name: 'description',
                    content: this.$language.translate(stepMetaMapper[this.currentStepId].description),
                },
            ],
            __dangerouslyDisableSanitizersByTagID: {
                description: ['content'],
            },
        };
    },
    watch: {
        isBasketEmpty: {
            handler(isBasketEmpty) {
                let doRedirect = false;
                if (isBasketEmpty) {
                    if (!this.$route.params.stepName || this.$route.params.stepName === undefined) {
                        doRedirect = true;
                    } else if (this.$route.params.stepName === 'address') {
                        doRedirect = true;
                    } else if (this.$route.params.stepName === 'summary') {
                        doRedirect = true;
                    }
                }
                if (doRedirect && (!this.currentStepId || this.currentStepId <= 2)) {
                    this.$router.push({ name: 'basket' });
                }
            },
            immediate: true
        }
    }
};
</script>
